/**
 * Calculates the offset of a pegboard item from the top-left corner of the pegboard.
 * @param cellSize - The size of a cell.
 * @param gap - The gap between cells.
 * @param targetIndex - The index of the target item.
 * @param scaleFactor - The scale factor of the item.
 * @returns The offset of the target item.
 */
export function getPegboardItemOffset(cellSize, gap, targetIndex, scaleFactor = 1) {
  // Since targetIndex on a pegboard is 1-based, we need to subtract by 1 since
  // the offset is the space between the item and the start of the pegboard.
  const index = targetIndex - 1;
  const gapOffset = index * gap * scaleFactor;
  const cellOffset = index * cellSize;
  return cellOffset + gapOffset;
}

/**
 * Calculates a dimension of a pegboard item along a single track.
 * @param cellSize - The size of a cell.
 * @param gap - The gap between cells.
 * @param spanned - The number of cells spanned by the item.
 * @param scaleFactor - The scale factor of the item.
 * @returns The dimension of the item.
 */
export function getPegboardItemDimension(cellSize, gap, spanned, scaleFactor = 1) {
  // Since gap is between items, we need to subtract spanned by 1. We also need
  // to apply the scale factor since we get gap from the layout which doesn't
  // factor in scale.
  const totalGap = gap * (spanned - 1) * scaleFactor;
  const totalSize = spanned * cellSize;
  return totalSize + totalGap;
}

/**
 * Rounds the given value to the nearest 100th decimal place
 * @param value number to round
 * @returns value rounded to nearest 100th decimal
 */
function roundToNearestHundredthDecimal(value) {
  return parseFloat(value.toFixed(2));
}

/**
 * Calculates the number of rows or columns an element on a pegboard should span.
 * @param elementSize - The height or width of the element.
 * @param cellSize - The size of a row or column in the pegboard.
 * @param gap - The size of the gap between cells.
 * @returns The number of rows or columns the element should span on the pegboard.
 */
export function getPegboardElementComputedSpanning({
  elementSize,
  cellSize,
  gap,
  maxSpan
}) {
  if (elementSize <= cellSize) return 1;
  const elementSizeAfterFirstCell = elementSize - cellSize;
  const roundedCellsSpanned = roundToNearestHundredthDecimal(elementSizeAfterFirstCell / (cellSize + gap));
  const computedSpanning = 1 + Math.ceil(roundedCellsSpanned);
  if (maxSpan) {
    return Math.min(computedSpanning, maxSpan);
  }
  return computedSpanning;
}

/**
 * Calculates the clamped position for pasting a pegboard item.
 * Applies an offset to a base position while ensuring the item stays within grid bounds.
 * @param rowStart - The starting row to offset from.
 * @param columnStart - The starting column to offset from.
 * @param offset - The offset to apply to the base position.
 * @param pegboardRows - The total number of rows in the pegboard.
 * @param pegboardColumns - The total number of columns in the pegboard.
 * @param rowsSpanned - The number of rows spanned by the item.
 * @param columnsSpanned - The number of columns spanned by the item.
 * @returns The clamped row and column start position.
 */
export function getPegboardPastePosition({
  rowStart,
  columnStart,
  offset,
  pegboardRows,
  pegboardColumns,
  rowsSpanned,
  columnsSpanned
}) {
  const maxValidRow = Math.max(1, pegboardRows - rowsSpanned + 1);
  const maxValidCol = Math.max(1, pegboardColumns - columnsSpanned + 1);
  return {
    rowStart: Math.max(1, Math.min(rowStart + offset, maxValidRow)),
    columnStart: Math.max(1, Math.min(columnStart + offset, maxValidCol))
  };
}